/* eslint-disable react/display-name */
//React
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import Basic from "./basic"
import SelectField from "./select"
import Button from "./button"
import AsyncCreatableSelect from 'react-select/async-creatable';
import { InputTextarea } from "primereact/inputtextarea";
import ToggleSwitch from "./toggle";
import { getCustomDataByTag } from "../../services/tag";
import useRefitOnFontLoad from "../../hooks/useRefitOnFontLoad";

import x from "../../assets/svgs/x-green.svg";
import * as CONST from "../../utilities/constants";
import email from "../../assets/svgs/eyeopen.svg";
import "./querybuilder.scss";

export function QueryBuilderValueEditor({ field, value, handleOnChange, type, inputType, operator, fieldData, rule }) {
	const [min, setMin] = useState(Array.isArray(value) ? value[0] : '');
	const [max, setMax] = useState(Array.isArray(value) ? value[1] : '');

	const handleMinChange = (newVal) => {
		setMin(newVal);
		handleOnChange([newVal, max]);
	};

	const handleMaxChange = (newVal) => {
		setMax(newVal);
		handleOnChange([min, newVal]);
	};

	const formatNumber = (num) => {
		const formatter = Intl.NumberFormat("en-Us", { notation: "compact" });
		return formatter.format(num);
	};

	const [selectedValue, setSelectedValue] = useState(value);
	const [hasLoadedOptions, setHasLoadedOptions] = useState(false);
	const [isLoadingOptions, setIsLoadingOptions] = useState(false);
	const [cachedOptions, setCachedOptions] = useState([]);
	const [isMultiValue, setIsMultiValue] = useState(
		() => Array.isArray(value) && operator !== "between"
	);
	const basicRef = useRef();
	const basicMinRef = useRef();
	const basicMaxRef = useRef();
	const selectRef = useRef();
	const multiValueRef = useRef(null);
	useRefitOnFontLoad(multiValueRef);

	useEffect(() => {
		if (selectedValue != value) {
			setSelectedValue(value);
			if (basicRef && basicRef.current) {
				basicRef.current.select(value);
			}
			if (basicMinRef && basicMinRef.current) {
				setMin(Array.isArray(value) ? value[0] : '');
				basicMinRef.current.select(Array.isArray(value) ? value[0] : '');
			}
			if (basicMaxRef && basicMaxRef.current) {
				setMax(Array.isArray(value) ? value[1] : '');
				basicMaxRef.current.select(Array.isArray(value) ? value[1] : '');
			}
			if (selectRef && selectRef.current) {
				selectRef.current.select(CONST.queryBuilderBooleanOptions.filter((el) => el.value === value)[0]);
			}
			// Sync multi-value state when value changes externally
			if (operator !== "between") {
				setIsMultiValue(Array.isArray(value));
			}
		}
	}, [value]);

	useEffect(() => {
		setHasLoadedOptions(false);
		setCachedOptions([]);
	}, [field]);

	const loadOptions = async (query) => {
        const _response = await getCustomDataByTag(fieldData.tagId, fieldData.customDataType, field, query, fieldData.eventName);
        const _list = _response.results.map((el) => {
			let m = structuredClone(fieldData.valueMap.filter((v) => v.value === el.value)[0]) ?? {
				label: `${el.value}`,
				value: el.value,
			};
			m.pageViews = el.page_views_last_30_days;
			return m;
		});
        return _list;
    };

	const handleMenuOpen = async () => {
		if (!hasLoadedOptions) {
			setIsLoadingOptions(true);
			const options = await loadOptions('');
			setCachedOptions(options);
			setHasLoadedOptions(true);
			setIsLoadingOptions(false);
		}
	};

	return (
        <>
		{(() => {
			if (type === "checkbox") {
				return (
					<SelectField
						name={field}
						options={CONST.queryBuilderBooleanOptions}
						selected={CONST.queryBuilderBooleanOptions.filter((el) => el.value === value)[0]}
						onSubmit={(selected) => handleOnChange(selected.value)}
						required={true}
						ref={selectRef}
					/>
				)
			} else if (operator === "between") {
				return (
					<div className={`${inputType === "number" ? "inline-fields" : ""}`}>
						<Basic
							name={field}
							value={min}
							onInput={(e) => handleMinChange(inputType === "number" ? Number(e.target.value) : e.target.value)}
							required={true}
							disabled={false}
							type={inputType}
							placeholder={"Min"}
							ref={basicMinRef}
						/>
						<Basic
							name={field}
							value={max}
							onInput={(e) => handleMaxChange(inputType === "number" ? Number(e.target.value) : e.target.value)}
							required={true}
							disabled={false}
							type={inputType}
							placeholder={"Max"}
							ref={basicMaxRef}
						/>
					</div>
				)
			} else if (fieldData.autocomplete === true) {
				const multiValueOperators = ["=", "!=", "contains", "doesNotContain", "beginsWith", "endsWith"];
				const supportsMultiValue = fieldData.multiValue === true && inputType === "string" && multiValueOperators.includes(operator);

				const handleMultiValueToggle = (checked) => {
					setIsMultiValue(checked);
					if (checked) {
						const current = Array.isArray(value) ? value : (value ? [value] : []);
						handleOnChange(current);
					} else {
						const first = Array.isArray(value) ? (value[0] || "") : (value || "");
						setSelectedValue(first);
						handleOnChange(first);
					}
				};

				const handleTextareaChange = (e) => {
					const lines = e.target.value.split("\n");
					handleOnChange(lines);
				};

				const handleTextareaBlur = () => {
					if (Array.isArray(value)) {
						const filtered = value.filter((l) => l.trim() !== "");
						handleOnChange(filtered.length > 0 ? filtered : []);
					}
				};

				const showMultiValue = isMultiValue && supportsMultiValue;

				return (
					<div className={`value-editor-multi${showMultiValue ? " multi-active" : ""}`}>
						{!showMultiValue && (
							<div className="input-wrapper required">
								<AsyncCreatableSelect
									name={field}
									classNamePrefix="select"
									className="select-field"
									value={fieldData.valueMap.filter((el) => el.value === selectedValue)[0] ?? {label: `${selectedValue}`, value: selectedValue}}
									defaultOptions={cachedOptions}
									loadOptions={(inputValue) => new Promise((resolve) => {resolve(loadOptions(inputValue))})}
									onChange={(newValue) => [setSelectedValue(newValue.value), handleOnChange(newValue.value)]}
									onMenuOpen={handleMenuOpen}
									isLoading={isLoadingOptions}
									isDisabled={false}
									required={true}
									isMulti={false}
									hideSelectedOptions={false}
									menuPlacement="auto"
									closeMenuOnSelect={true}
									placeholder='Type to filter'
									theme={(theme) => ({
										...theme,
										borderRadius: 0,
										colors: {
											...theme.colors,
											primary25: "#B3CF79",
											primary: "#9ACA3C",
										}
									})}
									key={field}
									formatOptionLabel={(data, formatOptionLabelMeta) => (formatOptionLabelMeta.context === "value" ? data.label : (
										<div className="taxonomy-configuration">
											<div>{data.label}</div>
											<div className="taxonomy-configuration-item">
												<img src={email}
													className="taxonomy-configuration-icon"
												/>
												<span>{data.pageViews ? formatNumber(data.pageViews) : "-"}</span>
											</div>
										</div>
									)
									)}
								/>
							</div>
						)}
						{supportsMultiValue && (
							<div className="multi-value-toggle-row">
								{showMultiValue && (
									<span className="multi-value-hint">One value per line</span>
								)}
								<div className="multi-value-toggle-right">
									<ToggleSwitch
										id={`multi-value-${rule?.id || field}`}
										label="Multiple Values"
										showLabel={true}
										size="sm"
										checked={isMultiValue}
										onInput={handleMultiValueToggle}
										required={false}
									/>
								</div>
							</div>
						)}
						{showMultiValue && (
							<div className="multi-value-textarea-wrapper">
								<InputTextarea
									ref={multiValueRef}
									value={Array.isArray(value) ? value.join("\n") : (value || "")}
									onChange={handleTextareaChange}
									onBlur={handleTextareaBlur}
									rows={6}
									placeholder={"value1\nvalue2\nvalue3"}
									autoResize
								/>
							</div>
						)}
					</div>
				)
			} else {
				return (
					<Basic
						name={field}
						value={selectedValue}
						onInput={(e) => [setSelectedValue(e.target.value),handleOnChange(e.target.value)]}
						required={true}
						disabled={false}
						type={inputType}
						ref={basicRef}
					/>
				)
			}
		})()}
        </>
	)
}

export function QueryBuilderSelectField({ field, options, value, handleOnChange }) {
	const [selected, setSelected] = useState(options.filter((el) => el.value === value)[0]);

	const selectRef = useRef();
	useEffect(() => {
		if ((selected ?? {}).value != value) {
			setSelected(options.filter((el) => el.value === value)[0]);
			if (selectRef && selectRef.current) {
				selectRef.current.select(options.filter((el) => el.value === value)[0]);
			}
		}
	}, [value]);

	return (
		<SelectField
			name={field}
			options={options}
			selected={selected}
			onSubmit={(selected) => [setSelected(selected), handleOnChange(selected.value)]}
			required={true}
			ref={selectRef}
		/>
	);
};

export function QueryBuilderCombinatorSelector({ field, options, value, handleOnChange, rules }) {
	const [selectedValue, setSelectedValue] = useState(value);

	const selectRef = useRef();
	useEffect(() => {
		if (value != selectedValue) {
			setSelectedValue(value);
			if (selectRef && selectRef.current) {
				selectRef.current.select(options.filter((el) => el.value === value)[0])
			}
		}
	}, [value]);

	return (
		<div className="combinatorSelector">
			{rules && rules.length ?
				(
					<>
						<SelectField
							name={field}
							options={options}
							selected={options.filter((el) => el.value === value)[0]}
							onSubmit={(selected) => {
								setSelectedValue(selected.value);
								handleOnChange(selected.value);
							}}
							required={true}
							search={false}
							ref={selectRef}
						/>
					</>
				) : ""
			}
		</div>
	)
};

export function QueryBuilderButton({ label, handleOnClick }) {
	return (
		<Button
			text={label}
			type="button"
			style="btn-simple"
			onClick={handleOnClick}
			disabled={false}
			loaded={false}
		/>
	);
};

export function QueryBuilderCloseButton({ handleOnClick }) {
	return (
		<img
			src={x}
			className="close"
			alt="close"
			role={"button"}
			onClick={() => handleOnClick()}
		/>
	)
};

QueryBuilderValueEditor.propTypes = {
	field: PropTypes.string,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
	handleOnChange: PropTypes.func,
	type: PropTypes.string,
	inputType: PropTypes.string,
	operator: PropTypes.string,
	fieldData: PropTypes.any,
	rule: PropTypes.shape({
		id: PropTypes.string,
	})
};

QueryBuilderSelectField.propTypes = {
	field: PropTypes.string,
	options: PropTypes.array,
	value: PropTypes.any,
	handleOnChange: PropTypes.func
}

QueryBuilderCombinatorSelector.propTypes = {
	field: PropTypes.string,
	options: PropTypes.array,
	value: PropTypes.any,
	handleOnChange: PropTypes.func,
	rules: PropTypes.array
}

QueryBuilderButton.propTypes = {
	label: PropTypes.string.isRequired,
	handleOnClick: PropTypes.func
}

QueryBuilderCloseButton.propTypes = {
	handleOnClick: PropTypes.func
}
